import * as THREE from "three/webgpu";
import {SketchEntityKind} from '../../sketch/solver/constraint/relation-compatibility/SketchEntityKinds';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import type {V2} from '../../interface/V2';
import {LineMaterialFactory, PointsMaterialFactory} from '../../material';
import type {SketchCollectionObject} from './SketchCollectionObject';
import {AbstractSketchEdgeObject} from './AbstractSketchEdgeObject';
import {SketchPointKind} from './ISketchObject';
import {SketchPointObject} from './SketchPointObject';
import {SketchLineObject} from './SketchLineObject';
import {SketchContour} from '../meshProxy/SketchContour';
import type {SketchSlotObject} from './SketchSlotObject';
import {isSketchSlotObject} from '../guards/isSketchSlotObject';
import type {SketchCloneContext} from './SketchCloneContext';
import {isStaleWorkerResponseError, workerManager} from '../../worker/WorkerManager';
import {
    DEFAULT_SKETCH_BEZIER_CURVE_METADATA,
    SketchBezierCurveClosureKind,
    SketchBezierCurveKind,
    type SketchBezierCurveMetadata,
    type SketchSplineObjectOptions
} from './SketchSplineObject';
import {normalizeSplineInterpolationPoints} from '../../tool/normalizeSplineInterpolationPoints';
import {
    CLOSED_SPLINE_CURVE_SAMPLE_OPTIONS,
    resolveCurveSampleSegments,
    SKETCH_DISPLAY_CURVE_SAMPLE_OPTIONS,
    SKETCH_PREVIEW_CURVE_SAMPLE_OPTIONS,
} from '../../tool/resolveCurveSampleSegments';
import {sampleCurvePointsSynchronously} from '../../tool/sampleCurvePointsSynchronously';

import {markOwnerSketchGroupDirty} from './utils/markOwnerSketchGroupDirty';
import {disposeSketchContour} from './clearSketchContourDisplays';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../../sketch/config/SketchGeometryToleranceConfig';
import {cloneSketchBSplineEvaluationDefinition} from '../../utils/sketch/cloneSketchBSplineEvaluationDefinition';
import type {SketchEdgeGeometryRefreshOptions} from '../../../types/mesh/sketch/SketchEdgeGeometryRefreshTypes';

/**
 * 判断对象是否为草图样式样条曲线对象。
 * @param value 待判断的 Three 对象。
 * @returns 当对象满足草图贝塞尔协议时返回 true。
 */

/**
 * 草图样式样条曲线对象。
 * 职责：
 * 1. 使用采样点序列表达贝塞尔曲线几何；
 * 2. 维护控制点、参考面与闭合集合关联；
 * 3. 提供曲线刷新与轮廓委托能力。
 */
export class SketchStyleSplineObject extends AbstractSketchEdgeObject {
    /** 对象运行时类型标记。 */
    public readonly objectType = SketchEntityKind.Spline;
    /** 样式样条在自闭合或归属闭合集合时视为封闭几何。 */
    public get isClosedGeometry(): boolean {
        return this.curveMetadata.closureKind === SketchBezierCurveClosureKind.Closed
            || this.closedCollection !== null;
    }
    /** 创建时所属抽象参考面。 */
    public referencePlane: ReferencePlane;
    /** 样式样条曲线运行时类型标记。 */
    public readonly isSketchStyleSplineObject: boolean = true;
    /** 样式样条曲线起点控制点。 */
    public startPoint: SketchPointObject;
    /** 样式样条曲线终点控制点。 */
    public endPoint: SketchPointObject;
    /** 样式样条曲线控制点列表。 */
    private controlPoints: SketchPointObject[] = [];
    /** 采样后的曲线点列表。 */
    private curvePoints: THREE.Vector3[] = [];
    /** 曲线元数据。 */
    private curveMetadata: SketchBezierCurveMetadata;
    /** Worker 导出的权威 B 样条求值定义。 */
    private curveEvaluationDefinition: SketchBSplineEvaluationDefinition | null = null;
    /** 关联的封闭线段几何对象。 */
    public closedCollection: SketchCollectionObject | SketchSlotObject | null = null;

    /** 控制点驱动的异步曲线重建版本号。 */
    private curveRebuildVersion: number = 0;
    /** 当前处于拖拽留影态的关联控制点数量。 */
    private relatedPointDragPreviewCount: number = 0;
    /** 拖拽期间是否积累了待执行的最终曲线重建。 */
    private hasDeferredCurveRebuildDuringDrag: boolean = false;


    /** 样式样条辅助控制折线列表。 */
    private readonly auxiliaryControlLines: SketchLineObject[] = [];


    /** 实例计数，用于生成显示名称。 */
    public static count = 0;

    public get baseDisplayName(): string {
        return '样式样条曲线';
    }

    /**
     * 构造草图样式样条曲线对象。
     * @param options 构造参数。
     */
    public constructor(options: SketchSplineObjectOptions) {
        super(options);
        this.referencePlane = options.referencePlane;
        this.startPoint = options.startPoint;
        this.endPoint = options.endPoint;
        this.startPoint.attachEndpointRole();
        this.endPoint.attachEndpointRole();
        this.controlPoints = this.normalizeControlPoints(options.controlPoints);
        this.curveMetadata = this.normalizeCurveMetadata(options.curveMetadata);
        this.curveEvaluationDefinition = cloneSketchBSplineEvaluationDefinition(
            options.curveEvaluationDefinition,
        );
        this.registerAllControlPoints();
        this.attachAllControlPoints();
        this.curvePoints = this.resolveInitialCurvePoints(options.curvePoints);
        this.name = `${this.baseDisplayName}${SketchStyleSplineObject.count}`;
        SketchStyleSplineObject.count += 1;
        this.updateGeometry();
    }

    protected getEdgeStartPoint(): SketchPointObject {
        return this.startPoint;
    }

    protected getEdgeEndPoint(): SketchPointObject {
        return this.endPoint;
    }

    /**
     * 获取贝塞尔控制点列表。
     * @returns 控制点列表副本。
     */
    public getControlPoints(): SketchPointObject[] {
        return [...this.controlPoints];
    }

    /**
     * 替换开放样式样条起点的拓扑点对象。
     *
     * @param point 新起点对象。
     */
    public setStartPoint(point: SketchPointObject): void {
        if (point === this.startPoint) {
            return;
        }

        const interiorPoints = this.controlPoints.slice(1, -1);
        this.startPoint.detachEndpointRole();
        this.startPoint = point;
        this.startPoint.attachEndpointRole();
        this.setControlPoints(interiorPoints);
    }

    /**
     * 替换开放样式样条终点的拓扑点对象。
     *
     * @param point 新终点对象。
     */
    public setEndPoint(point: SketchPointObject): void {
        if (point === this.endPoint) {
            return;
        }

        const interiorPoints = this.controlPoints.slice(1, -1);
        this.endPoint.detachEndpointRole();
        this.endPoint = point;
        this.endPoint.attachEndpointRole();
        this.setControlPoints(interiorPoints);
    }

    /**
     * 获取当前采样曲线点列表。
     * @returns 曲线点列表副本。
     */
    public getCurvePoints(): THREE.Vector3[] {
        return this.curvePoints.map((point) => point.clone());
    }

    /**
     * 获取控制点在参考面局部坐标系下的二维坐标列表。
     * @returns 控制点二维坐标列表。
     */
    public getControlPoints2D(): V2[] {
        return this.getControlPoints().map((point) => {
            const localPoint = this.referencePlane.worldToLocal(point.getPosition());
            return {
                x: localPoint.x,
                y: localPoint.y,
            };
        });
    }

    /**
     * 获取当前曲线元数据。
     * @returns 曲线元数据副本。
     */
    public getCurveMetadata(): SketchBezierCurveMetadata {
        return {
            ...this.curveMetadata,
            weights: [...this.curveMetadata.weights],
        };
    }

    /**
     * 获取 Worker 导出的权威 B 样条求值定义。
     * @returns 独立定义副本；当前曲线无需或尚未生成该定义时返回 null。
     */
    public getCurveEvaluationDefinition(): SketchBSplineEvaluationDefinition | null {
        return cloneSketchBSplineEvaluationDefinition(this.curveEvaluationDefinition);
    }

    /**
     * 更新当前曲线元数据。
     * @param metadata 新的曲线元数据。
     */
    public setCurveMetadata(metadata: Partial<SketchBezierCurveMetadata>): void {
        this.curveMetadata = this.normalizeCurveMetadata({
            ...this.curveMetadata,
            ...metadata,
            weights: metadata.weights ?? this.curveMetadata.weights,
        });
        // 曲线类型或控制定义变化后，旧 Worker 求值定义不再具有权威性，等待新结果重新写入。
        if (this.curveMetadata.curveKind !== SketchBezierCurveKind.Spline) {
            this.curveEvaluationDefinition = null;
        }
    }

    /**
     * 使当前对象尚未提交的异步曲线重建结果失效。
     *
     * 事务回滚已经恢复权威曲线定义时，只允许淘汰旧 Worker 结果，不能再次排队重算，
     * 否则回滚后的采样点仍可能被事务期间生成的派生结果覆盖。
     */
    public invalidatePendingCurveRebuild(): void {
        this.curveRebuildVersion += 1;
    }

    /**
     * 按新控制点、采样点和元数据重建当前样条对象。
     * @param options 重建参数。
     * @throws 当控制点或曲线采样点不是数组，或数量少于两个时抛出错误。
     */
    public rebuildByCurveDefinition(options: {
        /** 新控制点世界坐标列表。 */
        controlPointPositions: THREE.Vector3[];
        /** 新采样曲线点列表。 */
        curvePoints: THREE.Vector3[];
        /** 新曲线元数据。 */
        curveMetadata: Partial<SketchBezierCurveMetadata>;
        /** 与新曲线对应的权威 B 样条求值定义。 */
        curveEvaluationDefinition?: SketchBSplineEvaluationDefinition;
    }): void {
        // 权威定义写入前先使尚未返回的 Worker 结果失效，防止 Undo/Redo 或镜向被旧曲线覆盖。
        this.invalidatePendingCurveRebuild();
        if (!Array.isArray(options.controlPointPositions) || options.controlPointPositions.length < 2) {
            throw new Error('重建样条对象失败：控制点数量不足。');
        }
        if (!Array.isArray(options.curvePoints) || options.curvePoints.length < 2) {
            throw new Error('重建样条对象失败：曲线采样点数量不足。');
        }

        const nextControlPointPositions = options.controlPointPositions.map((point) => point.clone());
        this.startPoint.syncPositionSilently(nextControlPointPositions[0]);
        this.endPoint.syncPositionSilently(nextControlPointPositions[nextControlPointPositions.length - 1]);

        const currentInteriorPoints = this.controlPoints.slice(1, -1);
        const nextInteriorPointPositions = nextControlPointPositions.slice(1, -1);
        const nextInteriorPoints: SketchPointObject[] = nextInteriorPointPositions.map((point, index) => {
            const existingPoint = currentInteriorPoints[index] ?? null;
            if (existingPoint) {
                existingPoint.syncPositionSilently(point);
                return existingPoint;
            }
            return this.createControlPoint(point);
        });

        this.setCurveMetadata(options.curveMetadata);
        this.curveEvaluationDefinition = cloneSketchBSplineEvaluationDefinition(
            options.curveEvaluationDefinition,
        );
        // 完整曲线定义已经提供权威采样点，替换控制点时不得再启动异步重算覆盖该几何。
        this.replaceControlPoints(nextInteriorPoints, false);
        this.updateCurvePoints(options.curvePoints);
    }

    /**
     * 更新指定控制点在参考面局部坐标系下的坐标值，并重建曲线。
     * @param index 控制点索引。
     * @param axis 局部坐标轴。
     * @param value 新坐标值。
     * @throws 当当前样式样条的控制点数量少于两个时抛出错误。
     */
    public async rebuildByControlPointCoordinate(
        index: number,
        axis: 'x' | 'y',
        value: number,
    ): Promise<void> {
        const currentControlPoints = this.getControlPoints();
        if (currentControlPoints.length < 2) {
            throw new Error('样条控制点数量不足，无法重建曲线。');
        }
        const targetIndex = Math.min(Math.max(0, Math.floor(index)), currentControlPoints.length - 1);
        const nextControlPointPositions = currentControlPoints.map((point) => point.getPosition());
        const targetLocalPoint = this.referencePlane.worldToLocal(nextControlPointPositions[targetIndex]);
        if (axis === 'x') {
            targetLocalPoint.x = value;
        } else {
            targetLocalPoint.y = value;
        }
        nextControlPointPositions[targetIndex] = this.referencePlane.localToWorld(targetLocalPoint);
        const computedCurve = await this.computeCurvePointsByMetadata(nextControlPointPositions, this.curveMetadata);
        this.rebuildByCurveDefinition({
            controlPointPositions: nextControlPointPositions,
            curvePoints: computedCurve.curvePoints,
            curveMetadata: this.curveMetadata,
            curveEvaluationDefinition: computedCurve.evaluationDefinition,
        });
    }

    /**
     * 按控制点与曲线元数据重建当前样条对象。
     * @param controlPointPositions 新控制点世界坐标列表。
     * @param curveMetadata 新曲线元数据。
     */
    public async rebuildByControlPointDefinition(
        controlPointPositions: THREE.Vector3[],
        curveMetadata: Partial<SketchBezierCurveMetadata>,
    ): Promise<void> {
        const nextCurveMetadata = this.normalizeCurveMetadata({
            ...this.curveMetadata,
            ...curveMetadata,
            weights: curveMetadata.weights ?? this.curveMetadata.weights,
        });
        const computedCurve = await this.computeCurvePointsByMetadata(controlPointPositions, nextCurveMetadata);
        this.rebuildByCurveDefinition({
            controlPointPositions,
            curvePoints: computedCurve.curvePoints,
            curveMetadata: nextCurveMetadata,
            curveEvaluationDefinition: computedCurve.evaluationDefinition,
        });
    }


    /**
     * 复制当前贝塞尔对象，返回与源对象完全解耦的新实例。
     * @param context 复制上下文。
     * @returns 当前贝塞尔对象的独立副本。
     * @throws 当复制后的控制点不足以提供起点和终点时抛出错误。
     */
    public cloneSelf(context?: SketchCloneContext): SketchStyleSplineObject {
        const clonedControlPoints = this.controlPoints.map((point) => point.cloneSelf(context));
        const clonedStartPoint = clonedControlPoints[0];
        const clonedEndPoint = clonedControlPoints[clonedControlPoints.length - 1];
        if (!clonedStartPoint || !clonedEndPoint) {
            throw new Error('贝塞尔控制点数量非法，无法复制。');
        }

        const clonedBezier = new SketchStyleSplineObject({
            ...this.createCloneBaseOptions(),
            referencePlane: this.referencePlane,
            startPoint: clonedStartPoint,
            endPoint: clonedEndPoint,
            controlPoints: clonedControlPoints.slice(1, -1),
            curvePoints: this.getCurvePoints(),
            curveMetadata: this.getCurveMetadata(),
            curveEvaluationDefinition: this.getCurveEvaluationDefinition() ?? undefined,
        });
        this.registerObjectClone(context, clonedBezier);

        // 复制贝塞尔对象上的单目标几何关系。
        this.cloneSingleTargetConstraintsTo(clonedBezier, context);
        this.syncCommonDisplayStateToClone(clonedBezier);
        return clonedBezier;
    }


    /**
     * 替换贝塞尔控制点列表。
     * @param controlPoints 新控制点列表。
     */
    public setControlPoints(controlPoints: SketchPointObject[]): void {
        this.replaceControlPoints(controlPoints, true);
    }

    /**
     * 替换样式样条控制点，并按调用场景决定是否重新计算曲线。
     *
     * 设计说明：
     * 1. 无论是否重算曲线，辅助控制折线都必须同步到新的控制点拓扑；
     * 2. 交互式控制点变更需要 Worker 重新计算真实样条；
     * 3. `rebuildByCurveDefinition` 已携带权威采样点，禁止额外异步重算覆盖裁剪后的曲线。
     *
     * @param controlPoints 新控制点列表。
     * @param shouldScheduleCurveRebuild 是否按新控制点请求曲线重算。
     */
    private replaceControlPoints(
        controlPoints: SketchPointObject[],
        shouldScheduleCurveRebuild: boolean,
    ): void {
        this.detachAllControlPoints();
        this.unregisterAllControlPoints();
        this.controlPoints = this.normalizeControlPoints(controlPoints);
        this.registerAllControlPoints();
        this.attachAllControlPoints();

        // 控制点拓扑变化后，同步正式辅助折线结构。
        this.syncAuxiliaryControlLines();


        if (shouldScheduleCurveRebuild) {
            // 仅控制点变更时由 Worker 重新计算真实 Spline/BSpline 几何。
            this.scheduleCurveRebuildFromControlPoints();
        }
    }

    /**
     * 更新采样曲线点并刷新几何。
     * @param curvePoints 新曲线点列表。
     * @param options 派生几何刷新选项。
     * @throws 当曲线点不是数组或数量少于两个时抛出错误。
     */
    public updateCurvePoints(
        curvePoints: THREE.Vector3[],
        options?: SketchEdgeGeometryRefreshOptions,
    ): void {
        if (!Array.isArray(curvePoints) || curvePoints.length < 2) {
            throw new Error('贝塞尔曲线点数量不足，无法更新几何。');
        }
        this.curvePoints = curvePoints.map((point) => point.clone());
        this.updateGeometry(options);
    }


    /**
     * 判断当前样式样条对象是否允许拖动。
     * 说明：
     * 1. 先检查样式样条对象自身固定约束；
     * 2. 再检查所有控制点是否固定；
     * 3. 任一控制点固定时，整条样式样条视为不可拖动。
     * @returns 允许拖动返回 true，否则返回 false。
     */
    public override canDrag(): boolean {
        return super.canDrag()
            && this.controlPoints.every((point) => !point.isFixed());
    }


    /**
     * 同步端点到贝塞尔几何。
     * 说明：
     * 1. 端点或控制点变化后，重新按控制点采样曲线；
     * 2. 没有足够控制点时，退化为仅同步起终点。
     * @param movedPoint 触发刷新的控制点。
     */
    public override syncEndpointsToGeometry(movedPoint?: SketchPointObject): void {
        void movedPoint;

        // 端点或控制点被约束系统回写后，沿用创建时相同的 Worker 曲线求解路径。
        this.scheduleCurveRebuildFromControlPoints();

    }

    /**
     * 关联控制点进入拖拽留影态时，暂停样式样条的实时曲线重建。
     */
    public override onRelatedPointDragPreviewBegan(): void {
        if (this.relatedPointDragPreviewCount === 0) {
            /**
             * 交互开始时立刻使已有异步重建结果失效，
             * 避免拖拽前发出的 Worker 结果在拖拽中途回写旧曲线。
             */
            this.curveRebuildVersion += 1;
        }
        this.relatedPointDragPreviewCount += 1;
    }

    /**
     * 关联控制点退出拖拽留影态时，按最终控制点统一收口样式样条几何。
     */
    public override onRelatedPointDragPreviewEnded(): void {
        if (this.relatedPointDragPreviewCount <= 0) {
            return;
        }
        this.relatedPointDragPreviewCount -= 1;
        if (this.relatedPointDragPreviewCount > 0) {
            return;
        }
        if (!this.hasDeferredCurveRebuildDuringDrag) {
            return;
        }
        this.hasDeferredCurveRebuildDuringDrag = false;
        this.requestCurveRebuildFromControlPoints();
    }

    /**
     * 仅刷新当前边几何，不触发约束求解。
     *
     * 设计说明：
     * 1. 拖拽期间使用 CPU 轻量同步采样刷新显示曲线，确保预览实时跟手；
     * 2. 同时登记最终重建请求，拖拽结束后再以 Worker 精确采样结果统一收口；
     * 3. 非拖拽路径保留父类轻量刷新，并按控制点请求精确重建。
     * @param movedPoint 触发刷新的控制点。
     * @param options 派生几何刷新选项。
     */
    public override refreshGeometryOnly(
        movedPoint?: SketchPointObject,
        options?: SketchEdgeGeometryRefreshOptions,
    ): void {
        void movedPoint;
        if (this.isRelatedPointDragPreviewActive()) {
            this.hasDeferredCurveRebuildDuringDrag = true;
            // 拖拽期同步刷新显示曲线，保证曲线实时跟手；
            // 最终精确重建仍由松手时的 requestCurveRebuildFromControlPoints 统一收口。
            this.refreshDisplayCurvePointsSynchronously(options);
            return;
        }
        super.refreshGeometryOnly(undefined, options);
        // 非交互期按控制点重新采样：调用方刚静默改过控制点，只重建 buffer 会让曲线与控制点脱节。
        this.requestCurveRebuildFromControlPoints();
    }

    /**
     * 创建贝塞尔所属轮廓。
     * @returns 创建后的轮廓对象。
     * @throws 当非构造的样式样条未闭合时抛出错误。
     */
    public createContour(): SketchContour | null {

        // 混合闭合继续委托集合对象；自闭合样式样条直接基于自身几何生成轮廓。
        if (this.closedCollection) {
            const contour = this.closedCollection.createContour();
            if (!contour) {
                return null;
            }
            this.contour = contour;
            return contour;
        }
        if (this.isConstruction) {
            return null;
        }
        if (this.curveMetadata.closureKind !== SketchBezierCurveClosureKind.Closed) {
            throw new Error('当前样式样条未闭合，无法创建轮廓。');
        }
        this.updateGeometry();
        const contour = new SketchContour({
            source: this,
        });
        this.contour = contour;
        return contour;

    }

    /**
     * 规范化控制点列表。
     * @param controlPoints 外部传入控制点列表。
     * @returns 包含首尾端点的规范化控制点列表。
     */
    private normalizeControlPoints(controlPoints: SketchPointObject[]): SketchPointObject[] {
        const normalizedPoints: SketchPointObject[] = [];
        const sourcePoints = [this.startPoint, ...controlPoints, this.endPoint];
        sourcePoints.forEach((point) => {
            if (!point) {
                throw new Error('贝塞尔控制点不能为空。');
            }
            if (normalizedPoints.at(-1) === point) {
                return;
            }
            normalizedPoints.push(point);
        });
        if (normalizedPoints.length < 2) {
            throw new Error('贝塞尔控制点数量不足。');
        }
        return normalizedPoints;
    }

    /**
     * 根据当前采样曲线点刷新几何。
     * @param options 派生几何刷新选项。
     */
    protected updateGeometry(options?: SketchEdgeGeometryRefreshOptions): void {
        if (!Array.isArray(this.curvePoints) || this.curvePoints.length < 2) {
            throw new Error('贝塞尔曲线点数量不足，无法构建几何。');
        }

        const positionAttribute = this.geometry?.getAttribute('position');
        if (this.geometry && positionAttribute && positionAttribute.count === this.curvePoints.length) {
            // 拖拽期逐帧刷新等高频路径：点数一致时直接更新 position attribute，
            // 避免每帧重建 BufferGeometry 造成 GC 压力（渲染结果与重建完全等价）。
            this.curvePoints.forEach((point, index) => {
                positionAttribute.setXYZ(index, point.x, point.y, point.z);
            });
            positionAttribute.needsUpdate = true;
        } else {
            const geometry = new THREE.BufferGeometry().setFromPoints(this.curvePoints);
            const previousGeometry = this.geometry;
            this.geometry = geometry;
            if (previousGeometry) {
                previousGeometry.dispose();
            }
        }

        this.geometry.computeBoundingBox();
        this.geometry.computeBoundingSphere();

        if (this.shouldSyncLineDistancesForMaterial(this.material)) {
            this.computeLineDistances();
        }


        // 曲线几何刷新后，同步辅助折线端点位置，保证其跟随控制点更新。
        this.syncAuxiliaryControlLines();


        // 混合闭合交给集合刷新；自闭合样式样条直接刷新自身轮廓。
        if (this.closedCollection) {
            this.requestCompositeHostContourRefresh(this.closedCollection, options);
            return;
        }
        if (this.contour && this.contour.parent === this) {
            this.contour.syncSource();
        }

    }

    /**
     * 规范化曲线元数据。
     * @param metadata 外部传入曲线元数据。
     * @returns 规范化后的曲线元数据。
     */
    private normalizeCurveMetadata(metadata?: Partial<SketchBezierCurveMetadata>): SketchBezierCurveMetadata {
        const normalizedWeights = Array.isArray(metadata?.weights)
            ? metadata!.weights.map((weight) => {
                if (!Number.isFinite(weight)) {
                    return 1;
                }
                return Math.max(SKETCH_GEOMETRY_TOLERANCE_CONFIG.splineMinimumWeight, weight);
            })
            : [];
        return {
            selectionKind: metadata?.selectionKind ?? DEFAULT_SKETCH_BEZIER_CURVE_METADATA.selectionKind,
            curveKind: metadata?.curveKind ?? DEFAULT_SKETCH_BEZIER_CURVE_METADATA.curveKind,

            closureKind: metadata?.closureKind ?? DEFAULT_SKETCH_BEZIER_CURVE_METADATA.closureKind,

            styleSplineMode: metadata?.styleSplineMode ?? DEFAULT_SKETCH_BEZIER_CURVE_METADATA.styleSplineMode,
            degree: Number.isFinite(metadata?.degree ?? null) ? Number(metadata!.degree) : DEFAULT_SKETCH_BEZIER_CURVE_METADATA.degree,
            weights: normalizedWeights,
            isRational: metadata?.isRational
                ?? normalizedWeights.some(
                    (weight) => Math.abs(weight - 1) > SKETCH_GEOMETRY_TOLERANCE_CONFIG.splineRationalWeightDelta,
                ),
        };
    }


    /**
     * 解析曲线采样时应使用的控制点输入。
     * @param controlPointPositions 当前控制点位置列表。
     * @param curveMetadata 当前曲线元数据。
     * @returns 用于曲线采样的控制点位置列表。
     */
    private resolveCurveInputControlPointPositions(
        controlPointPositions: readonly THREE.Vector3[],
        curveMetadata: SketchBezierCurveMetadata,
    ): THREE.Vector3[] {
        const normalizedControlPointPositions = controlPointPositions.map((point) => point.clone());
        if (
            curveMetadata.closureKind !== SketchBezierCurveClosureKind.Closed
            || normalizedControlPointPositions.length < 2
        ) {
            return normalizedControlPointPositions;
        }
        return [
            ...normalizedControlPointPositions,
            normalizedControlPointPositions[0].clone(),
        ];
    }


    /**
     * 创建控制点对象。
     * @param position 控制点位置。
     * @returns 控制点对象。
     */
    private createControlPoint(position: THREE.Vector3): SketchPointObject {
        return new SketchPointObject({
            position: position.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane: this.referencePlane,
        });
    }


    /**
     * 同步样式样条正式辅助控制折线。
     * 说明：
     * 1. 辅助折线与控制点数量保持一致，段数 = 控制点数 - 1；
     * 2. 每段辅助线使用独立的辅助草图点作为端点，避免污染正式控制点的事件语义；
     * 3. 控制点位置变化时仅静默同步辅助线端点与几何，不修改正式控制点对象。
     */
    private syncAuxiliaryControlLines(): void {
        const expectedLineCount = Math.max(this.controlPoints.length - 1, 0);

        while (this.auxiliaryControlLines.length < expectedLineCount) {
            const nextLineIndex = this.auxiliaryControlLines.length;
            const auxiliaryLine = this.createAuxiliaryControlLine(
                this.controlPoints[nextLineIndex].getPosition(),
                this.controlPoints[nextLineIndex + 1].getPosition(),
            );
            this.auxiliaryControlLines.push(auxiliaryLine);
            this.add(auxiliaryLine);
        }

        while (this.auxiliaryControlLines.length > expectedLineCount) {
            const removedLine = this.auxiliaryControlLines.pop();
            if (!removedLine) {
                throw new Error('样式样条辅助控制折线移除失败。');
            }
            if (removedLine.parent === this) {
                this.remove(removedLine);
            }
            removedLine.geometry.dispose();
            if (removedLine.material instanceof THREE.Material) {
                removedLine.material.dispose();
            }
        }

        this.auxiliaryControlLines.forEach((auxiliaryLine, index) => {
            const startPoint = this.controlPoints[index];
            const endPoint = this.controlPoints[index + 1];
            if (!startPoint || !endPoint) {
                throw new Error('样式样条辅助控制折线缺少对应控制点。');
            }
            auxiliaryLine.startPoint.syncPositionSilently(startPoint.getPosition());
            auxiliaryLine.endPoint.syncPositionSilently(endPoint.getPosition());
            auxiliaryLine.syncEndpointsToGeometry();
        });
    }

    /**
     * 创建一段正式辅助控制折线。
     * @param startPosition 起点位置。
     * @param endPosition 终点位置。
     * @returns 辅助控制折线对象。
     */
    private createAuxiliaryControlLine(
        startPosition: THREE.Vector3,
        endPosition: THREE.Vector3,
    ): SketchLineObject {
        const startPoint = new SketchPointObject({
            position: startPosition.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane: this.referencePlane,
            isAuxiliaryObject: true,
        });
        const endPoint = new SketchPointObject({
            position: endPosition.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane: this.referencePlane,
            isAuxiliaryObject: true,
        });
        const auxiliaryLine = new SketchLineObject({
            startPoint,
            endPoint,
            material: LineMaterialFactory.createConstructionLineMaterial(),
            referencePlane: this.referencePlane,
            isConstruction: true,
            isAuxiliaryObject: true,
        });
        auxiliaryLine.computeLineDistances();
        return auxiliaryLine;
    }


    /**
     * 解析初始采样曲线点。
     * 说明：
     * 1. 外部提供了合法采样点时直接复用；
     * 2. 未提供或数量不足时，按当前控制点重新采样；
     * 3. 返回值始终为独立副本，禁止直接持有外部数组引用。
     * @param curvePoints 外部传入曲线点列表。
     * @returns 初始曲线点列表。
     */
    private resolveInitialCurvePoints(curvePoints?: THREE.Vector3[]): THREE.Vector3[] {
        if (Array.isArray(curvePoints) && curvePoints.length >= 2) {
            return curvePoints.map((point) => point.clone());
        }
        if (this.curveMetadata.curveKind !== SketchBezierCurveKind.Bezier) {
            throw new Error('样式样条缺少初始采样点，无法构造非贝塞尔曲线几何。');
        }
        return this.buildCurvePointsFromControlPoints();
    }

    /**
     * 注册所有控制点与当前贝塞尔对象的关联。
     */
    private registerAllControlPoints(): void {
        this.controlPoints.forEach((point) => {
            point.registerConnectedEdge(this);
            this.registerRelatedSketchPoint(point);
        });
    }

    /**
     * 解除所有控制点与当前贝塞尔对象的关联。
     */
    private unregisterAllControlPoints(): void {
        this.controlPoints.forEach((point) => {
            point.unregisterConnectedEdge(this);
            this.unregisterRelatedSketchPoint(point);
        });
    }

    /**
     * 将所有控制点挂载到当前对象下。
     */
    private attachAllControlPoints(): void {
        this.controlPoints.forEach((point) => {
            if (point.parent !== this) {
                this.add(point);
            }
        });
    }

    /**
     * 将所有控制点从当前对象下移除。
     */
    private detachAllControlPoints(): void {
        this.controlPoints.forEach((point) => {
            if (point.parent === this) {
                this.remove(point);
            }
        });
    }

    /**
     * 基于曲线元数据计算采样点。
     * @param controlPointPositions 控制点世界坐标列表。
     * @param curveMetadata 曲线元数据。
     * @returns 新采样曲线点与权威 B 样条求值定义。
     */
    private async computeCurvePointsByMetadata(
        controlPointPositions: THREE.Vector3[],
        curveMetadata: SketchBezierCurveMetadata,
    ): Promise<{
        curvePoints: THREE.Vector3[];
        evaluationDefinition?: SketchBSplineEvaluationDefinition;
    }> {
        const curveType = curveMetadata.curveKind === SketchBezierCurveKind.Spline
            ? '2d_spline'
            : curveMetadata.curveKind === SketchBezierCurveKind.BSpline
                ? '2d_bspline'
                : '2d_bezier';
        const normalizedControlPointPositions = curveMetadata.curveKind === SketchBezierCurveKind.Spline
            ? normalizeSplineInterpolationPoints(controlPointPositions)
            : controlPointPositions;

        // 自闭合样式样条在重建时同样需要把首点补到末尾，以维持闭合采样结果。
        const curveInputControlPointPositions = this.resolveCurveInputControlPointPositions(
            normalizedControlPointPositions,
            curveMetadata,
        );

        const segments = resolveCurveSampleSegments(
            curveInputControlPointPositions,
            curveMetadata.closureKind === SketchBezierCurveClosureKind.Closed
                ? CLOSED_SPLINE_CURVE_SAMPLE_OPTIONS
                : SKETCH_DISPLAY_CURVE_SAMPLE_OPTIONS,
        );
        const result = await workerManager.compute2DCurve({
            curveType,
            params: {
                points: curveInputControlPointPositions.map((point) => ({x: point.x, y: point.y, z: point.z})),
                segments,
                degree: curveMetadata.curveKind === SketchBezierCurveKind.BSpline ? (curveMetadata.degree ?? 3) : undefined,
                weights: curveMetadata.isRational ? curveMetadata.weights : undefined,
                closureKind: curveMetadata.closureKind,
            },
        });
        return {
            curvePoints: result.points.map((point) => new THREE.Vector3(point.x, point.y, point.z)),
            evaluationDefinition: cloneSketchBSplineEvaluationDefinition(result.evaluationDefinition) ?? undefined,
        };
    }

    /**
     * 计算指定参数位置上的贝塞尔点。
     * @param t 曲线参数，范围 [0, 1]。
     * @param controlPositions 控制点位置列表。
     * @returns 曲线点。
     */
    private computeBezierPoint(t: number, controlPositions: THREE.Vector3[]): THREE.Vector3 {
        const degree = controlPositions.length - 1;
        const result = new THREE.Vector3();
        for (let index = 0; index <= degree; index += 1) {
            const coefficient = this.computeBinomialCoefficient(degree, index);
            const blend = coefficient * Math.pow(1 - t, degree - index) * Math.pow(t, index);
            result.addScaledVector(controlPositions[index], blend);
        }
        return result;
    }

    /**
     * 计算二项式系数。
     * @param n 阶数。
     * @param k 当前项索引。
     * @returns 二项式系数。
     */
    private computeBinomialCoefficient(n: number, k: number): number {
        let result = 1;
        for (let index = 1; index <= k; index += 1) {
            result *= (n - index + 1) / index;
        }
        return result;
    }


    /**
     * 根据当前交互态决定是立即重建，还是延迟到拖拽结束后统一重建。
     */
    private scheduleCurveRebuildFromControlPoints(): void {
        // 开放插值样条的权威定义来自 Worker；控制点变化后必须立刻作废旧定义，避免捕捉到旧曲线。
        if (
            this.curveMetadata.curveKind === SketchBezierCurveKind.Spline
            && this.curveMetadata.closureKind === SketchBezierCurveClosureKind.Open
        ) {
            this.curveEvaluationDefinition = null;
        }
        if (this.isRelatedPointDragPreviewActive()) {
            this.hasDeferredCurveRebuildDuringDrag = true;
            // 拖拽期同步刷新显示曲线，保证曲线实时跟手；
            // 最终精确重建仍由松手时的 requestCurveRebuildFromControlPoints 统一收口。
            this.refreshDisplayCurvePointsSynchronously();
            return;
        }
        this.requestCurveRebuildFromControlPoints();
    }

    /**
     * 拖拽期 CPU 轻量同步刷新显示曲线。
     *
     * 设计说明：
     * 1. 不经过 Worker 异步往返，直接在 CPU 端按当前控制点同步采样并写回显示几何，
     *    使样式样条曲线在拖拽过程中实时跟手；
     * 2. 采样档位使用实时预览配置（SKETCH_PREVIEW_CURVE_SAMPLE_OPTIONS），
     *    控制逐帧刷新成本，松手后的精确重建仍使用显示档位；
     * 3. 轻量采样结果只作为拖拽期预览，松手时 `onRelatedPointDragPreviewEnded`
     *    会再次发起 Worker 精确重建覆盖本结果；
     * 4. 曲线几何刷新会连带同步辅助控制折线端点（updateGeometry 内统一收口）。
     * @param options 派生几何刷新选项。
     */
    private refreshDisplayCurvePointsSynchronously(options?: SketchEdgeGeometryRefreshOptions): void {
        if (this.controlPoints.length < 2) {
            return;
        }
        const controlPointPositions = this.controlPoints.map((point) => point.getPosition());
        const segments = resolveCurveSampleSegments(controlPointPositions, SKETCH_PREVIEW_CURVE_SAMPLE_OPTIONS);
        const sampledPoints = sampleCurvePointsSynchronously(controlPointPositions, this.curveMetadata, segments);
        if (sampledPoints.length < 2) {
            return;
        }
        this.updateCurvePoints(
            sampledPoints.map((point) => new THREE.Vector3(point.x, point.y, point.z)),
            options,
        );
    }

    /**
     * 判断当前是否存在关联控制点处于拖拽留影态。
     * @returns 只要存在一个关联控制点处于交互态即返回 true。
     */
    private isRelatedPointDragPreviewActive(): boolean {
        return this.relatedPointDragPreviewCount > 0;
    }

    /**
     * 基于当前控制点请求异步重建曲线采样点。
     *
     * 设计说明：
     * 1. 样式样条可以切换为 Spline/BSpline，最终曲线必须由 Worker 统一求解；
     * 2. 通过版本号丢弃过期结果，避免拖拽中的旧曲线覆盖新控制点；
     * 3. 采样结果落地后提交草图定义变更，让 profile rebuild 消费最终曲线。
     */
    private requestCurveRebuildFromControlPoints(): void {
        if (this.controlPoints.length < 2) {
            throw new Error('样式样条控制点数量不足，无法重建曲线。');
        }

        const currentVersion = this.curveRebuildVersion + 1;
        this.curveRebuildVersion = currentVersion;
        const controlPointPositions = this.controlPoints.map((point) => point.getPosition());

        void this.computeCurvePointsByMetadata(controlPointPositions, this.curveMetadata)
            .then((computedCurve) => {
                if (this.curveRebuildVersion !== currentVersion) {
                    return;
                }
                this.curveEvaluationDefinition = cloneSketchBSplineEvaluationDefinition(
                    computedCurve.evaluationDefinition,
                );
                this.updateCurvePoints(computedCurve.curvePoints);
                const ownerSketchGroup = markOwnerSketchGroupDirty(this, {
                    reason: 'style-spline-curve-rebuild-finished',
                    sourceObjectId: this.uuid,
                });
                // 样式样条的异步采样同样晚于拖拽事务边界，最终结果必须独立提交，
                // 防止下游 profile rebuild 在控制点已变化时继续消费旧曲线。
                ownerSketchGroup?.commitDefinitionChanges({
                    reason: 'style-spline-curve-rebuild-finished',
                    sourceObjectId: this.uuid,
                });
            })
            .catch((error) => {
                if (isStaleWorkerResponseError(error)) {
                    return;
                }
                console.error('样式样条控制点驱动重建失败:', error);
            });
    }


    /**
     * 绑定所属封闭线段几何对象。
     * @param geometry 目标封闭线段几何对象或槽口对象。
     */
    public attachClosedCollection(geometry: SketchCollectionObject | SketchSlotObject): void {
        if (this.closedCollection && this.closedCollection !== geometry) {
            console.trace('当前贝塞尔曲线已关联其他封闭线段几何对象。');
        }
        this.closedCollection = geometry;
        if (isSketchSlotObject(geometry)) {
            this.contour = geometry.contour;
            return;
        }
        this.contour = geometry.getContour();
    }

    /**
     * 解绑所属封闭线段几何对象。
     * @param geometry 若传入，仅在匹配时才解绑。
     */
    public detachClosedCollection(geometry?: SketchCollectionObject | SketchSlotObject): void {
        if (geometry && this.closedCollection !== geometry) {
            return;
        }
        this.closedCollection = null;
        this.clearContour();
    }

    /**
     * 贝塞尔对象是否可创建轮廓。
     * @returns 若已关联封闭线段几何对象则返回 true。
     */
    public canCreateContour(): boolean {
        return this.closedCollection !== null
            || this.curveMetadata.closureKind === SketchBezierCurveClosureKind.Closed;
    }

    /**
     * 基于控制点重新采样贝塞尔曲线点。
     * @returns 采样后的曲线点列表。
     */
    private buildCurvePointsFromControlPoints(): THREE.Vector3[] {
        if (this.controlPoints.length < 2) {
            return [
                this.startPoint.getPosition(),
                this.endPoint.getPosition(),
            ];
        }
        const sampledPoints: THREE.Vector3[] = [];

        // 样式样条本地重建时，闭合曲线同样需要补回首点以保持闭环。
        const controlPositions = this.resolveCurveInputControlPointPositions(
            this.controlPoints.map((point) => point.getPosition()),
            this.curveMetadata,
        );

        const segments = resolveCurveSampleSegments(controlPositions, SKETCH_DISPLAY_CURVE_SAMPLE_OPTIONS);
        for (let index = 0; index <= segments; index += 1) {
            const t = index / segments;
            sampledPoints.push(this.computeBezierPoint(t, controlPositions));
        }
        return sampledPoints;
    }


    /**
     * 同步当前样式样条轮廓显示。
     */
    public syncContour(): void {
        this.clearContourDisplay();
        const contour = this.createContour();
        if (!contour) {
            this.contour = null;
            return;
        }
        this.contour = contour;
        this.add(contour);
    }

    /**
     * 清理当前轮廓显示。
     */
    public clearContourDisplay(): void {
        const contour = this.contour;
        if (contour && contour.parent === this) {
            this.remove(contour);
        }
        if (contour) {
            disposeSketchContour(contour);
        }
        this.clearContour();
    }

}
